import React, { useEffect, useState } from 'react';
import { Button, Typography, Table, TableHead, TableRow, TableCell, TableBody, Box, TablePagination } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from '../../axiosInstance';
import { FaSearch } from "react-icons/fa";
import Profile from '../../components/Profile';
import Notification from '../../components/Notification';
import Input from '../../components/ui/Input';
import { saveAs } from 'file-saver';

const LibraryManagement = () => {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchBooks = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const collegeId = user?.collegeId;
  
      const res = await axios.get('https://backend.scholarkart.in/api/books/search-college', {
        params: { collegeId, limit: 500 }
      });
      setBooks(res.data.books || []);
    } catch (error) {
      console.error('Error fetching books:', error);
    }
  };

  useEffect(() => {
    fetchBooks();
  }, []);

  const filteredBooks = books.filter((book) => {
    const query = inputValue.toLowerCase();
    return (
      book.bookTitle?.toLowerCase().includes(query) ||
      book.authorName?.toLowerCase().includes(query) ||
      book.publisherName?.toLowerCase().includes(query) ||
      book.bookType?.toLowerCase().includes(query)
    );
  });

  const paginatedBooks = filteredBooks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportCSV = () => {
    const headers = ["Title", "Author", "Publisher", "Year", "Type", "Language"];
    const rows = filteredBooks.map(book => [
      book.bookTitle,
      book.authorName,
      book.publisherName,
      book.publicationYear,
      book.bookType,
      book.bookLanguage
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell || ''}"`).join(","))
    ].join("\n");

    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-GB').replace(/\//g, '-');
    const filename = `library_books_${formattedDate}.csv`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, filename);
  };

  const handleAddBook = () => navigate('/addbook');
  const handleDeleteBook = () => navigate('/deletebook');

  const handleEdit = (bookId) => {
    navigate(`/editbook/${bookId}`);
  };

  return (
    <main className="flex-grow p-1">
      <div className="flex justify-between items-center relative bg-white p-4 rounded-lg shadow-md">
        <div className="relative w-full max-w-lg">
          <Input
            placeholder="Search books..."
            className="pl-10"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
          <FaSearch className="absolute left-3 top-3 text-gray-500" />
        </div>
        <div className="flex items-center space-x-4 relative">
          <Notification />
          <Profile />
        </div>
      </div>

      <Box p={4}>
        <Typography variant="h4" fontWeight="bold" mb={4}>Library Books Management</Typography>

        <Box display="flex" justifyContent="flex-end" gap={2} mb={2}>
          <Button variant="contained" color="primary" onClick={handleAddBook}>Add Book</Button>
          <Button variant="contained" color="error" onClick={handleDeleteBook}>Delete Book</Button>
          <Button variant="contained" color="success" onClick={handleExportCSV}>Export Books</Button>
        </Box>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Author</TableCell>
              <TableCell>Publisher</TableCell>
              <TableCell>Year</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Language</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedBooks.map((book) => (
              <TableRow key={book._id}>
                <TableCell>{book.bookTitle}</TableCell>
                <TableCell>{book.authorName}</TableCell>
                <TableCell>{book.publisherName}</TableCell>
                <TableCell>{book.publicationYear}</TableCell>
                <TableCell>{book.bookType}</TableCell>
                <TableCell>{book.bookLanguage}</TableCell>
                <TableCell>
                  <Button variant="outlined" color="secondary" onClick={() => handleEdit(book._id)}>
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={filteredBooks.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 20, 50]}
        />
      </Box>
    </main>
  );
};

export default LibraryManagement;
